import React, { useState, useCallback } from 'react';
import axios from 'axios';
import { useDropzone } from 'react-dropzone';
import { useAuth } from '../contexts/AuthContext';

const API_URL = process.env.REACT_APP_API_URL || '${API_URL}';

function DocumentUpload() {
  const [uploading, setUploading] = useState(false);
  const [uploadResult, setUploadResult] = useState(null);
  const [error, setError] = useState(null);
  const [title, setTitle] = useState('');
  const [tags, setTags] = useState('');
  const { token } = useAuth();

  const onDrop = useCallback(async (acceptedFiles) => {
    if (acceptedFiles.length === 0) return;

    const file = acceptedFiles[0];
    setUploading(true);
    setError(null);
    setUploadResult(null);

    try {
      const formData = new FormData();
      formData.append('file', file);
      if (title) formData.append('title', title);
      formData.append('source', 'upload');
      if (tags) formData.append('tags', tags);

      const response = await axios.post(
        `${API_URL}/api/v1/index`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
            'Authorization': `Bearer ${token}`
          }
        }
      );

      setUploadResult(response.data);
      setTitle('');
      setTags('');
    } catch (err) {
      console.error('Error uploading file:', err);

      // Handle different error response formats
      let errorMessage = 'An error occurred while uploading the file';

      if (err.response?.data?.detail) {
        const detail = err.response.data.detail;

        // Check if detail is an object (structured error)
        if (typeof detail === 'object' && detail.message) {
          errorMessage = detail.message;

          // Add extra context for storage errors
          if (detail.error === 'Storage quota exceeded') {
            errorMessage += `\n\nFile size: ${detail.file_size_mb}MB\nAvailable space: ${detail.available_mb}MB\nStorage limit: ${detail.storage_limit_mb}MB`;
          }
        }
        // Check if detail is a simple string
        else if (typeof detail === 'string') {
          errorMessage = detail;
        }
      }
      // Fallback to generic error message
      else if (err.message) {
        errorMessage = err.message;
      }

      setError(errorMessage);
    } finally {
      setUploading(false);
    }
  }, [title, tags]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
      'text/plain': ['.txt'],
      'text/markdown': ['.md'],
      'text/csv': ['.csv']
    },
    maxFiles: 1,
    disabled: uploading
  });

  return (
    <div className="max-w-2xl mx-auto">
      <div className="bg-white rounded-lg shadow-lg p-8">
        <h2 className="text-2xl font-bold mb-6">Upload Documents</h2>

        {/* Optional Metadata */}
        <div className="mb-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Title (optional)
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Document title (auto-detected if empty)"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={uploading}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Tags (optional)
            </label>
            <input
              type="text"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              placeholder="ai, research, documentation (comma-separated)"
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              disabled={uploading}
            />
          </div>
        </div>

        {/* Dropzone */}
        <div
          {...getRootProps()}
          className={`border-2 border-dashed rounded-lg p-12 text-center cursor-pointer transition-colors ${
            isDragActive
              ? 'border-blue-500 bg-blue-50'
              : 'border-gray-300 hover:border-blue-400'
          } ${uploading ? 'opacity-50 cursor-not-allowed' : ''}`}
        >
          <input {...getInputProps()} />

          {uploading ? (
            <div>
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-gray-600">Uploading and indexing document...</p>
            </div>
          ) : isDragActive ? (
            <div>
              <svg className="mx-auto h-12 w-12 text-blue-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              <p className="mt-4 text-lg text-blue-600">Drop the file here</p>
            </div>
          ) : (
            <div>
              <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
              </svg>
              <p className="mt-4 text-lg text-gray-700">
                Drag and drop a file here, or click to select
              </p>
              <p className="mt-2 text-sm text-gray-500">
                Supported formats: PDF, DOCX, TXT, MD, CSV
              </p>
            </div>
          )}
        </div>

        {/* Success Message */}
        {uploadResult && !error && (
          <div className="mt-6 p-4 bg-green-100 border border-green-400 text-green-700 rounded-lg">
            <h3 className="font-semibold mb-2">✓ Upload Successful!</h3>
            <p className="text-sm">
              <strong>Title:</strong> {uploadResult.title}
            </p>
            <p className="text-sm">
              <strong>Chunks Indexed:</strong> {uploadResult.chunks_indexed}
            </p>
            <p className="text-sm">
              <strong>Document ID:</strong> {uploadResult.doc_id}
            </p>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="mt-6 p-4 bg-red-100 border border-red-400 text-red-700 rounded-lg">
            <h3 className="font-semibold mb-2">✗ Upload Failed</h3>
            <p className="text-sm whitespace-pre-line">{error}</p>
          </div>
        )}

        {/* Info */}
        <div className="mt-6 p-4 bg-blue-50 rounded-lg">
          <h4 className="font-semibold text-blue-900 mb-2">How it works:</h4>
          <ol className="list-decimal list-inside text-sm text-blue-800 space-y-1">
            <li>Upload your document (PDF, DOCX, TXT, MD, CSV)</li>
            <li>The document is automatically parsed and split into chunks</li>
            <li>Each chunk is embedded using AI for semantic search</li>
            <li>You can now query the document using natural language</li>
          </ol>
        </div>
      </div>
    </div>
  );
}

export default DocumentUpload;
